/* Convert measured bottle-face millimetres to the square product-preview stage.
 * The face excludes the lid. Width landmarks, when measured, calibrate the
 * horizontal photo projection independently. Solved artwork stays in millimetres.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.BloomProductCalibration = factory();
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const EPSILON = 1e-9;
  const MAX_ENGRAVING_WIDTH_MM = 60;
  function finite(value) { return typeof value === 'number' && Number.isFinite(value); }
  function rectangle(value, label) {
    if (!value || !['x', 'y', 'width', 'height'].every(key => finite(value[key])) ||
        value.width <= 0 || value.height <= 0 || value.x < -EPSILON || value.y < -EPSILON ||
        value.x + value.width > 1 + EPSILON || value.y + value.height > 1 + EPSILON) {
      throw new RangeError(label + ' must be a positive rectangle inside its normalized image or stage.');
    }
    return value;
  }
  function resolve(photo) {
    if (!photo || typeof photo !== 'object') throw new TypeError('A calibrated product photo is required.');
    const calibration = photo.faceCalibration;
    if (!calibration || !finite(calibration.heightMm) || calibration.heightMm <= 0 ||
        !finite(calibration.topY) || !finite(calibration.bottomY) ||
        calibration.topY < 0 || calibration.bottomY > 1 || calibration.topY >= calibration.bottomY) {
      throw new RangeError('Bottle-face calibration needs a measured height and ordered top and bottom landmarks.');
    }
    const maximumHeight = String(photo.size).toLowerCase().replace(/\s/g, '') === '40oz' ? 180 : 170;
    if (!finite(photo.widthMm) || !finite(photo.heightMm) || photo.widthMm <= 0 || photo.widthMm > MAX_ENGRAVING_WIDTH_MM || photo.heightMm <= 0 ||
        photo.heightMm > Math.min(calibration.heightMm, maximumHeight)) {
      throw new RangeError('The engraving area must fit the measured bottle face and the size-specific 60 mm wide engraving limit.');
    }
    const crop = rectangle(photo.crop, 'The photo crop');
    const frame = rectangle(photo.stageCropRect, 'The photo frame');
    const savedArea = rectangle(photo.stageArea, 'The saved Studio area');
    if (calibration.topY < crop.y - EPSILON || calibration.bottomY > crop.y + crop.height + EPSILON) {
      throw new RangeError('Both bottle-face landmarks must remain visible inside the photo crop.');
    }
    const top = frame.y + (calibration.topY - crop.y) / crop.height * frame.height;
    const bottom = frame.y + (calibration.bottomY - crop.y) / crop.height * frame.height;
    const height = bottom - top;
    const scale = height / calibration.heightMm;
    const centerY = (top + bottom) / 2;
    // A 40 oz proof can reuse its matching 32 oz bottle image. Its taller face
    // changes only vertical millimetres per pixel; the body width is unchanged.
    const widthReferenceHeight = calibration.projectionWidthReferenceHeightMm ?? calibration.heightMm;
    if (!finite(widthReferenceHeight) || widthReferenceHeight <= 0) throw new RangeError('The horizontal projection reference must be positive.');
    let centerX = savedArea.x + savedArea.width / 2, scaleX = height / widthReferenceHeight;
    let horizontal = {};
    const hasWidth = ['widthMm', 'leftX', 'rightX'].some(key => calibration[key] !== undefined);
    if (hasWidth) {
      if (!finite(calibration.widthMm) || calibration.widthMm <= 0 ||
          !finite(calibration.leftX) || !finite(calibration.rightX) ||
          calibration.leftX < 0 || calibration.rightX > 1 || calibration.leftX >= calibration.rightX ||
          photo.widthMm > Math.min(calibration.widthMm, MAX_ENGRAVING_WIDTH_MM)) {
        throw new RangeError('Measured cup width needs ordered landmarks and an engraving width of at most 60 mm.');
      }
      if (calibration.leftX < crop.x - EPSILON || calibration.rightX > crop.x + crop.width + EPSILON) {
        throw new RangeError('Both cup-width landmarks must remain visible inside the photo crop.');
      }
      const left = frame.x + (calibration.leftX - crop.x) / crop.width * frame.width;
      const right = frame.x + (calibration.rightX - crop.x) / crop.width * frame.width;
      scaleX = (right - left) / calibration.widthMm;
      centerX = (left + right) / 2;
      horizontal = { left, right, width: right - left, widthMm: calibration.widthMm };
    }
    const width = photo.widthMm * scaleX;
    const areaHeight = photo.heightMm * scale;
    const region = {
      x: centerX - width / 2, y: centerY - areaHeight / 2,
      width, height: areaHeight
    };
    rectangle(region, 'The calibrated engraving area');
    return {
      region,
      physicalArea: { widthMm: photo.widthMm, heightMm: photo.heightMm },
      face: { top, bottom, heightMm: calibration.heightMm, height, centerY, centerX, ...horizontal },
      scale, scaleX, scaleY: scale,
      widthCalibrated: hasWidth,
      marginMm: (calibration.heightMm - photo.heightMm) / 2,
      calibrated: true
    };
  }
  function photoForSize(photo, size) {
    if (!photo) return null;
    const normalized = String(size).toLowerCase().replace(/\s/g, '');
    const sourceSize = String(photo.size).toLowerCase().replace(/\s/g, '');
    if (normalized === sourceSize) return photo;
    if (normalized !== '40oz' || sourceSize !== '32oz') {
      throw new RangeError('Only the matching 32 oz photo may be reused for a 40 oz proof.');
    }
    resolve(photo);
    const calibration = photo.faceCalibration;
    const result = {
      ...photo, id: photo.id.replace(/32oz/i, '40oz') + '-32oz-preview', size: '40oz',
      widthMm: MAX_ENGRAVING_WIDTH_MM, heightMm: 180,
      previewSourceSize: '32oz', previewSourceId: photo.id,
      faceCalibration: {
        ...calibration, heightMm: 210,
        projectionWidthReferenceHeightMm: calibration.projectionWidthReferenceHeightMm ?? calibration.heightMm,
        reference: '40 oz cup face below the closed-lid collar to the base; lid excluded.',
        method: 'Merchant 210 mm face measurement. Preview reuses the matching-color 32 oz image and its landmarks; artwork is projected using the 40 oz physical limits.',
        verifiedManufacturingCalibration: false
      }
    };
    resolve(result);
    return result;
  }
  return Object.freeze({ resolve, photoForSize });
});
